import type { CSSProperties } from 'react'
import type {
  RouletteWinnerShareCardRoulette,
  RouletteWinnerShareCardRoulettePrize,
} from '@/lib/roulette'
import {
  buildCampaignBrandPalette,
  campaignBrandPaletteToStyle,
  resolveCampaignBrandColor,
} from '@/lib/campaignBrandPalette'
import styles from './RouletteWinnerShareWheelBackdrop.module.css'

export interface RouletteWinnerShareWheelBackdropProps {
  roulette: RouletteWinnerShareCardRoulette | null
  primaryColor: string | null
  buttonColor: string | null
}

function buildWheelGradient(positions: number, sliceA: string, sliceB: string): string {
  if (positions <= 0) {
    return `conic-gradient(from -90deg, ${sliceA} 0deg 360deg)`
  }

  const step = 360 / positions
  const stops: string[] = []
  for (let index = 0; index < positions; index += 1) {
    const color = index % 2 === 0 ? sliceA : sliceB
    const start = index * step
    const end = (index + 1) * step
    stops.push(`${color} ${start}deg ${end}deg`)
  }

  return `conic-gradient(from -90deg, ${stops.join(', ')})`
}

function getSliceLabel(prize: RouletteWinnerShareCardRoulettePrize): string {
  const label = prize.display.label
  if (typeof label === 'string' && label.trim() !== '') {
    return label.trim()
  }

  if (prize.prize_type === 'coupon') {
    return 'Cupom'
  }
  if (prize.prize_type === 'pix') {
    return 'PIX'
  }
  if (prize.prize_type === 'lucky_number') {
    const quantity = prize.display.quantity
    if (typeof quantity === 'number' && quantity > 1) {
      return `${quantity} números`
    }
    return 'Nº sorte'
  }

  return '😞'
}

function resolveSlicePrizes(
  roulette: RouletteWinnerShareCardRoulette
): RouletteWinnerShareCardRoulettePrize[] {
  const positions = Math.max(roulette.positions, roulette.prizes.length, 1)
  const byPosition = new Map<number, RouletteWinnerShareCardRoulettePrize>()

  for (const prize of roulette.prizes) {
    byPosition.set(prize.position, prize)
  }

  return Array.from({ length: positions }, (_, index) => {
    const position = index + 1
    return (
      byPosition.get(position) ?? {
        position,
        prize_type: 'none',
        display: { label: '😞' },
      }
    )
  })
}

export function RouletteWinnerShareWheelBackdrop({
  roulette,
  primaryColor,
  buttonColor,
}: RouletteWinnerShareWheelBackdropProps) {
  const palette = buildCampaignBrandPalette(resolveCampaignBrandColor(primaryColor, buttonColor))
  const brandStyle = campaignBrandPaletteToStyle(palette) as CSSProperties

  if (!roulette || roulette.prizes.length === 0) {
    return (
      <div className={styles.backdrop} aria-hidden>
        <div className={styles.wheelEmpty} style={brandStyle} />
      </div>
    )
  }

  const slices = resolveSlicePrizes(roulette)
  const positions = slices.length
  const sliceAngle = 360 / positions
  const labelRadius = 320

  const wheelStyle: CSSProperties = {
    ...brandStyle,
    background: buildWheelGradient(positions, palette.sliceA, palette.sliceB),
  }

  return (
    <div className={styles.backdrop} aria-hidden>
      <div className={styles.wheelGlow} style={brandStyle} />
      <div className={styles.wheelOuter} style={brandStyle}>
        <div className={styles.wheelLights}>
          {Array.from({ length: 16 }, (_, index) => {
            const angle = index * (360 / 16) * (Math.PI / 180)
            const radius = 478
            const x = Math.sin(angle) * radius
            const y = -Math.cos(angle) * radius

            return (
              <span
                key={index}
                className={index % 2 === 0 ? styles.lightOn : styles.lightOff}
                style={{
                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                }}
              />
            )
          })}
        </div>

        <div className={styles.wheelPointer} style={brandStyle} />

        <div className={styles.wheelSpinner} style={wheelStyle}>
          {slices.map((slice, index) => {
            const centerAngle = index * sliceAngle + sliceAngle / 2
            const needsFlip = centerAngle > 90 && centerAngle < 270
            const label = getSliceLabel(slice)

            return (
              <div
                key={`${slice.position}-${index}`}
                className={styles.sliceLabel}
                style={{
                  transform: `translate(-50%, -50%) rotate(${centerAngle}deg) translateY(-${labelRadius}px) rotate(${needsFlip ? 180 : 0}deg) rotate(-90deg)`,
                }}
              >
                <span className={styles.sliceLabelText}>{label}</span>
              </div>
            )
          })}

          <div className={styles.wheelInnerRing} style={brandStyle} />
        </div>
      </div>
    </div>
  )
}
